import { For, Show, onCleanup, type JSX } from "solid-js";
import { cx } from "../../styled-system/css";
import { imageUrl } from "../data";
import { categoryDisplayName } from "../categories";
import { canManageCatalogItem, catalogItemDisplayStatus } from "../features/catalog/selectors";
import { styles } from "../styles";
import type { CoordEntry, CoordItem } from "../types";
import { OwnershipCheckbox } from "./OwnershipCheckbox";
import { QrRegistrationButton } from "./QrRegistrationButton";
import { CoordDetailsButton } from "./CoordDetailsButton";
import { BrandChip } from "./BrandChip";
import { coordinateOwnershipStats } from "../features/catalog/ownership";
import { OwnershipSummary } from "./OwnershipSummary";
import { fullImageMatchesColor, itemMatchesColor } from "../search";
import type { ColorFilters } from "../types";

type Props = {
  entry: CoordEntry;
  isSelected: (item: CoordItem) => boolean;
  onToggle: (item: CoordItem) => void;
  onPreviewEntry: (entry: CoordEntry) => void;
  onPreviewItem: (item: CoordItem) => void;
  canUpdateOwnership: boolean;
  showItemQr: boolean;
  isOwnershipPending: (item: CoordItem) => boolean;
  onOwnershipChange: (item: CoordItem, owned: boolean) => void;
  onQrRegistration: (item: CoordItem) => void;
  showParentImage?: boolean;
  parentImageThumbnailUrl?: string;
  previewParentOnClick?: boolean;
  title?: string;
  metadata?: JSX.Element;
  actions?: JSX.Element;
  onBrandFilter?: (brand: string) => void;
  plainBrand?: boolean;
  colorFilters?: ColorFilters;
};

function itemPlacement(term: string): { "grid-row"?: string; "grid-column"?: string } {
  if (term === "トップス" || term === "ワンピ") return { "grid-row": "1", "grid-column": "1" };
  if (term === "ボトムス") return { "grid-row": "1", "grid-column": "2" };
  if (term === "シューズ") return { "grid-row": "2", "grid-column": "1" };
  if (term === "アクセ") return { "grid-row": "2", "grid-column": "2" };
  return {};
}

function ItemTile(props: {
  item: CoordItem;
  displayOwned: boolean;
  displayQr: string;
  selected: boolean;
  onToggle: () => void;
  onPreview: () => void;
  canUpdateOwnership: boolean;
  showQrControl: boolean;
  ownershipPending: boolean;
  onOwnershipChange: (owned: boolean) => void;
  onQrRegistration: () => void;
  colorMatched: boolean;
}) {
  let timer: number | undefined;
  let startX = 0;
  let startY = 0;
  let longPressed = false;
  let moved = false;
  const cancelTimer = () => {
    if (timer !== undefined) window.clearTimeout(timer);
    timer = undefined;
  };
  onCleanup(cancelTimer);

  return (
    <div
      class={cx(styles.childTile, !props.canUpdateOwnership && !props.showQrControl && styles.childTileWithoutStatus)}
      style={itemPlacement(props.item.term)}
    >
      <button
        class={cx(
          styles.childButton,
          props.colorMatched && styles.colorMatchedFrame,
          props.selected && styles.childSelected,
        )}
        type="button"
        title={`${props.item.term} (${props.item.id})・クリックで選択、長押しで拡大`}
        aria-pressed={props.selected}
        aria-label={`${props.item.name} ${props.item.term}。クリックで${props.selected ? "選択解除" : "選択"}、長押しで拡大表示`}
        onPointerDown={(event) => {
          if (event.button !== 0) return;
          startX = event.clientX;
          startY = event.clientY;
          moved = false;
          longPressed = false;
          cancelTimer();
          timer = window.setTimeout(() => {
            longPressed = true;
            props.onPreview();
            navigator.vibrate?.(30);
          }, 550);
        }}
        onPointerMove={(event) => {
          if (Math.hypot(event.clientX - startX, event.clientY - startY) > 10) {
            moved = true;
            cancelTimer();
          }
        }}
        onPointerUp={cancelTimer}
        onPointerCancel={cancelTimer}
        onPointerLeave={cancelTimer}
        onContextMenu={(event) => event.preventDefault()}
        onClick={(event) => {
          if (longPressed || moved) {
            event.preventDefault();
            longPressed = false;
            moved = false;
            return;
          }
          props.onToggle();
        }}
      >
        <img class={cx(styles.image, styles.thumbnailImage)} src={imageUrl(props.item.url)} alt={`${props.item.name} ${props.item.term}`} loading="lazy" decoding="async" draggable={false} />
        <Show when={props.selected}><span class={styles.selectedBadge} title="選択中" aria-label="選択中">✓</span></Show>
      </button>
      <Show when={props.canUpdateOwnership || props.showQrControl}>
        <div
          class={cx(styles.itemStatus, !(props.canUpdateOwnership && props.showQrControl) && styles.itemStatusSingle)}
          aria-label={props.showQrControl ? "取得状況とQR登録状況" : "取得状況"}
        >
          <Show when={props.canUpdateOwnership}>
            <OwnershipCheckbox
              checked={props.displayOwned}
              disabled={props.ownershipPending}
              label={`${props.item.name} ${props.item.term}を${props.displayOwned ? "未所持" : "所持済み"}に変更`}
              onChange={props.onOwnershipChange}
            />
          </Show>
          <Show when={props.showQrControl}>
            <QrRegistrationButton
              registered={Boolean(props.displayQr)}
              label={props.displayQr
                ? `${props.item.name} ${props.item.term}のQRを表示`
                : `${props.item.name} ${props.item.term}のQRを登録`}
              onClick={props.onQrRegistration}
            />
          </Show>
        </div>
      </Show>
    </div>
  );
}

function ParentImageTile(props: { entry: CoordEntry; thumbnailUrl?: string; previewOnClick?: boolean; onPreview: () => void; colorMatched: boolean }) {
  let timer: number | undefined;
  let startX = 0;
  let startY = 0;
  const cancelTimer = () => {
    if (timer !== undefined) window.clearTimeout(timer);
    timer = undefined;
  };
  onCleanup(cancelTimer);
  const hasImage = () => Boolean(props.entry.url.trim());

  return (
    <button
      class={cx(styles.parentFrame, props.colorMatched && styles.colorMatchedFrame)}
      type="button"
      title={hasImage() ? `${props.entry.name}・${props.previewOnClick ? "クリックで詳細表示" : "長押しで拡大"}` : `${props.entry.name}・画像無し`}
      aria-label={hasImage() ? `${props.entry.name}。${props.previewOnClick ? "クリックで詳細表示" : "長押しで拡大表示"}` : `${props.entry.name}。画像無し`}
      onPointerDown={(event) => {
        if (event.button !== 0 || !hasImage() || props.previewOnClick) return;
        startX = event.clientX;
        startY = event.clientY;
        cancelTimer();
        timer = window.setTimeout(() => {
          props.onPreview();
          navigator.vibrate?.(30);
        }, 550);
      }}
      onPointerMove={(event) => {
        if (Math.hypot(event.clientX - startX, event.clientY - startY) > 10) {
          cancelTimer();
        }
      }}
      onPointerUp={cancelTimer}
      onPointerCancel={cancelTimer}
      onPointerLeave={cancelTimer}
      onContextMenu={(event) => event.preventDefault()}
      onClick={(event) => {
        event.preventDefault();
        if (hasImage() && props.previewOnClick) props.onPreview();
      }}
      onKeyDown={(event) => {
        if (hasImage() && (event.key === "Enter" || event.key === " ") && !event.repeat) {
          event.preventDefault();
          props.onPreview();
        }
      }}
    >
      <Show
        when={hasImage()}
        fallback={<span class={styles.parentImagePlaceholder}>画像無し</span>}
      >
        <img
          class={cx(styles.image, styles.thumbnailImage)}
          src={imageUrl(props.thumbnailUrl || props.entry.url)}
          alt={`${props.entry.name} コーデ`}
          loading="lazy"
          decoding="async"
          draggable={false}
          onError={(event) => {
            const image = event.currentTarget;
            if (!props.thumbnailUrl || image.dataset.fallbackAttempted === "true") return;
            image.dataset.fallbackAttempted = "true";
            image.src = imageUrl(props.entry.url);
          }}
        />
      </Show>
    </button>
  );
}

export function CoordCard(props: Props) {
  const ownership = () => coordinateOwnershipStats(props.entry.items);
  const showParentImage = () => props.showParentImage !== false;

  return (
    <article class={styles.card} aria-label={props.title ?? props.entry.name} data-catalog-entry-key={props.entry.key}>
        <div class={styles.cardTitle}>
          <div class={styles.cardHeadingRow}>
            <h2 class={styles.cardHeading}>{props.title ?? props.entry.name}</h2>
            <CoordDetailsButton title={props.title ?? props.entry.name} onClick={() => props.onPreviewEntry(props.entry)} />
          </div>
          <Show when={props.metadata} fallback={(
            <p class={styles.meta}>
              Brand: <BrandChip brand={props.entry.brand} onSelect={props.onBrandFilter} plain={props.plainBrand} />　★{props.entry.star || "-"}　Cat: {categoryDisplayName(props.entry.category)}
              <Show when={props.entry.series === "primagi"}>　使用: {props.entry.available === true ? "可" : props.entry.available === false ? "不可" : "不明"}</Show>
              <Show when={props.entry.mix}>　区分: {props.entry.mix?.scope === "global" ? "共有" : "自分用"}</Show>
              <Show when={props.canUpdateOwnership && ownership().known}>　取得: <OwnershipSummary items={props.entry.items} /></Show>
            </p>
          )}>{props.metadata}</Show>
          <Show when={props.actions}>{props.actions}</Show>
        </div>
        <div class={cx(styles.images, !showParentImage() && styles.imagesWithoutParent)}>
          <Show when={showParentImage()}>
            <ParentImageTile
              entry={props.entry}
              thumbnailUrl={props.parentImageThumbnailUrl}
              previewOnClick={props.previewParentOnClick}
              onPreview={() => props.onPreviewEntry(props.entry)}
              colorMatched={Boolean(props.colorFilters && fullImageMatchesColor(props.entry, props.colorFilters))}
            />
          </Show>
          <div class={styles.childGrid}>
            <For each={props.entry.items}>
              {(item) => {
                const status = catalogItemDisplayStatus(props.entry, item);
                const canManageStatus = props.canUpdateOwnership && canManageCatalogItem(props.entry, item);
                const showQrControl = props.showItemQr && canManageCatalogItem(props.entry, item);
                return <ItemTile
                  item={item}
                  displayOwned={status.owned}
                  displayQr={status.qr}
                  selected={props.isSelected(item)}
                  onToggle={() => props.onToggle(item)}
                  onPreview={() => props.onPreviewItem(item)}
                  canUpdateOwnership={canManageStatus}
                  showQrControl={showQrControl}
                  ownershipPending={props.isOwnershipPending(item)}
                  onOwnershipChange={(owned) => props.onOwnershipChange(item, owned)}
                  onQrRegistration={() => props.onQrRegistration(item)}
                  colorMatched={Boolean(props.colorFilters && itemMatchesColor(item, props.colorFilters))}
                />;
              }}
            </For>
          </div>
        </div>
    </article>
  );
}
